import React, { useState, useEffect } from 'react';
import {
  TextField,
  Card,
  CardContent,
  Typography,
  Avatar,
  Box,
  Chip,
  Grid,
  Paper,
  Divider,
  IconButton,
  Button,
  Autocomplete,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  Event as EventIcon,
  FitnessCenter as FitnessCenterIcon,
  Pool as PoolIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  MonetizationOn as MonetizationOnIcon
} from '@mui/icons-material';
import './MemberSearch.css';
import { memberSearchService } from '../../services';

const MemberSearch = () => {
  const [selectedMember, setSelectedMember] = useState(null);
  const [searchOptions, setSearchOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [membershipDuration, setMembershipDuration] = useState('');

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    const controller = new AbortController();

    if (!debouncedSearchTerm) {
      setSearchOptions([]);
      return () => controller.abort();
    }

    const fetchSearchResults = async () => {
      try {
        const response = await memberSearchService.searchMembers({
          search: debouncedSearchTerm,
          limit: 12,
          signal: controller.signal,
        });

        if (!controller.signal.aborted) {
          setSearchOptions(response.members || []);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error searching members:', error);
        }
      }
    };

    fetchSearchResults();

    return () => controller.abort();
  }, [debouncedSearchTerm]);

  const calculateMembershipDuration = (startDate) => {
    if (!startDate) return 'N/A';
    
    const start = new Date(startDate);
    const now = new Date();
    const diffTime = Math.abs(now - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 30) {
      return `${diffDays} days`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      const remainingDays = diffDays % 30;
      return remainingDays > 0 ? `${months} months, ${remainingDays} days` : `${months} months`;
    } else {
      const years = Math.floor(diffDays / 365);
      const remainingMonths = Math.floor((diffDays % 365) / 30);
      return remainingMonths > 0 ? `${years} years, ${remainingMonths} months` : `${years} years`;
    }
  };

  const calculateDaysUntilDue = (dueDate) => {
    if (!dueDate) return 'N/A';
    
    const due = new Date(dueDate);
    const now = new Date();
    const diffTime = due - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return `Overdue by ${Math.abs(diffDays)} days`;
    } else if (diffDays === 0) {
      return 'Due today';
    } else {
      return `${diffDays} days remaining`;
    }
  };

  const handleMemberSelect = (event, newValue) => {
    setSelectedMember(newValue);
    if (newValue) {
      setMembershipDuration(calculateMembershipDuration(newValue.Startdate));
    } else {
      setMembershipDuration('');
    }
  };

  const handleSearchInputChange = (event, newInputValue, reason) => {
    setSearchTerm(newInputValue);

    if (reason === 'clear') {
      setSelectedMember(null);
      setSearchOptions([]);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'clear':
        return 'success';
      case 'pending':
        return 'error';
      default:
        return 'default';
    }
  };

  const getMemberTypeIcon = (type) => {
    return type === 'gym' ? <FitnessCenterIcon /> : <PoolIcon />;
  };

  const getMemberTypeColor = (type) => {
    return type === 'gym' ? '#6d2057' : '#2196f3';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <Box sx={{ 
      padding: { xs: 1, sm: 2, md: 3 }, 
      width: '100%', 
      maxWidth: '100%',
      margin: '0 auto',
      boxSizing: 'border-box'
    }} className="member-search-container">
      <Typography variant="h4" gutterBottom sx={{ 
        color: '#6d2057', 
        fontWeight: 'bold',
        fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
        textAlign: { xs: 'center', sm: 'left' }
      }}>
        Member Search & Profile
      </Typography>
      
      <Paper elevation={3} sx={{ 
        padding: { xs: 2, sm: 3 }, 
        marginBottom: 3,
        width: '100%',
        boxSizing: 'border-box'
      }}>
        <Autocomplete
          options={searchOptions}
          getOptionLabel={(option) => `${option.Name} - ${option.Mobile} (${option.memberType.toUpperCase()})`}
          isOptionEqualToValue={(option, value) => option._id === value._id && option.memberType === value.memberType}
          renderOption={(props, option) => (
            <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getMemberTypeIcon(option.memberType)}
              <Box>
                <Typography variant="body1">{option.Name}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {option.Mobile} • {option.memberType.toUpperCase()} Member
                </Typography>
              </Box>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search name or mobile"
              variant="outlined"
              fullWidth
            />
          )}
          value={selectedMember}
          inputValue={searchTerm}
          onChange={handleMemberSelect}
          onInputChange={handleSearchInputChange}
          filterOptions={(options) => options}
          noOptionsText={debouncedSearchTerm ? 'No matching members' : 'Start typing'}
          sx={{ marginBottom: 2 }}
        />
      </Paper>

      {selectedMember && (
        <Card elevation={4} className="member-profile-card">
          <CardContent>
            {/* Header Section */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'center', sm: 'center' }, 
              marginBottom: 3,
              textAlign: { xs: 'center', sm: 'left' }
            }}>
              <Avatar
                src={selectedMember.Image}
                sx={{ 
                  width: { xs: 80, sm: 100 }, 
                  height: { xs: 80, sm: 100 }, 
                  marginRight: { xs: 0, sm: 3 },
                  marginBottom: { xs: 2, sm: 0 },
                  border: `3px solid ${getMemberTypeColor(selectedMember.memberType)}`
                }}
              >
                <PersonIcon sx={{ fontSize: { xs: 40, sm: 50 } }} />
              </Avatar>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h4" sx={{ 
                  color: getMemberTypeColor(selectedMember.memberType), 
                  fontWeight: 'bold',
                  fontSize: { xs: '1.5rem', sm: '2rem' }
                }}>
                  {selectedMember.Name}
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'center', sm: 'flex-start' }, 
                  gap: 1, 
                  marginTop: 1 
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getMemberTypeIcon(selectedMember.memberType)}
                    <Chip 
                      label={`${selectedMember.memberType.toUpperCase()} Member`}
                      color="primary"
                      sx={{ backgroundColor: getMemberTypeColor(selectedMember.memberType) }}
                    />
                  </Box>
                  <Chip 
                    label={selectedMember.FeesStatus || 'Unknown'}
                    color={getStatusColor(selectedMember.FeesStatus)}
                    variant="outlined"
                  />
                </Box>
              </Box>
            </Box>

            <Divider sx={{ marginBottom: 3 }} />

            {/* Main Information Grid */}
            <Grid container spacing={3}>
              {/* Personal Information */}
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ padding: 2, height: '100%' }}>
                  <Typography variant="h6" sx={{ color: '#6d2057', marginBottom: 2, fontWeight: 'bold' }}>
                    Personal Information
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                    <PersonIcon sx={{ marginRight: 1, color: '#6d2057' }} />
                    <Typography><strong>Age:</strong> {selectedMember.Age || 'N/A'} years</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                    <PhoneIcon sx={{ marginRight: 1, color: '#6d2057' }} />
                    <Typography><strong>Mobile:</strong> {selectedMember.Mobile}</Typography>
                    <IconButton 
                      size="small" 
                      href={`tel:${selectedMember.Mobile}`}
                      sx={{ marginLeft: 1 }}
                    >
                      <PhoneIcon sx={{ fontSize: 20, color: '#6d2057' }} />
                    </IconButton>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                    <LocationIcon sx={{ marginRight: 1, color: '#6d2057' }} />
                    <Typography><strong>Address:</strong> {selectedMember.Address || 'N/A'}</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                    <WorkIcon sx={{ marginRight: 1, color: '#6d2057' }} />
                    <Typography><strong>Occupation:</strong> {selectedMember.Occupation || 'N/A'}</Typography>
                  </Box>
                </Paper>
              </Grid>

              {/* Membership Information */}
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ padding: 2, height: '100%' }}>
                  <Typography variant="h6" sx={{ color: '#6d2057', marginBottom: 2, fontWeight: 'bold' }}>
                    Membership Information
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                    <EventIcon sx={{ marginRight: 1, color: '#6d2057' }} />
                    <Typography><strong>Start Date:</strong> {formatDate(selectedMember.Startdate)}</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                    <EventIcon sx={{ marginRight: 1, color: '#6d2057' }} />
                    <Typography><strong>Due Date:</strong> {formatDate(selectedMember.Duedate)}</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                    <AccessTimeIcon sx={{ marginRight: 1, color: '#6d2057' }} />
                    <Typography><strong>Membership Duration:</strong> {membershipDuration}</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                    <AccessTimeIcon sx={{ marginRight: 1, color: '#6d2057' }} />
                    <Typography>
                      <strong>Payment Status:</strong> {calculateDaysUntilDue(selectedMember.Duedate)}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                    <MonetizationOnIcon sx={{ marginRight: 1, color: '#6d2057' }} />
                    <Typography><strong>Pending Fees:</strong> ₹{selectedMember.PendingFees || 0}</Typography>
                  </Box>
                </Paper>
              </Grid>

              {/* Body Measurements (if available) */}
              {(selectedMember.Weight || selectedMember.Chest || selectedMember.Waist) && (
                <Grid item xs={12}>
                  <Paper elevation={2} sx={{ padding: 2 }}>
                    <Typography variant="h6" sx={{ color: '#6d2057', marginBottom: 2, fontWeight: 'bold' }}>
                      Body Measurements
                    </Typography>
                    <Grid container spacing={2}>
                      {selectedMember.Weight && (
                        <Grid item xs={6} sm={4} md={3}>
                          <Typography><strong>Weight:</strong> {selectedMember.Weight}</Typography>
                        </Grid>
                      )}
                      {selectedMember.Chest && (
                        <Grid item xs={6} sm={4} md={3}>
                          <Typography><strong>Chest:</strong> {selectedMember.Chest}</Typography>
                        </Grid>
                      )}
                      {selectedMember.Waist && (
                        <Grid item xs={6} sm={4} md={3}>
                          <Typography><strong>Waist:</strong> {selectedMember.Waist}</Typography>
                        </Grid>
                      )}
                      {selectedMember.Hip && (
                        <Grid item xs={6} sm={4} md={3}>
                          <Typography><strong>Hip:</strong> {selectedMember.Hip}</Typography>
                        </Grid>
                      )}
                      {selectedMember.Arm && (
                        <Grid item xs={6} sm={4} md={3}>
                          <Typography><strong>Arm:</strong> {selectedMember.Arm}</Typography>
                        </Grid>
                      )}
                      {selectedMember.Thigh && (
                        <Grid item xs={6} sm={4} md={3}>
                          <Typography><strong>Thigh:</strong> {selectedMember.Thigh}</Typography>
                        </Grid>
                      )}
                      {selectedMember.Stomach && (
                        <Grid item xs={6} sm={4} md={3}>
                          <Typography><strong>Stomach:</strong> {selectedMember.Stomach}</Typography>
                        </Grid>
                      )}
                      {selectedMember.LowerTier && (
                        <Grid item xs={6} sm={4} md={3}>
                          <Typography><strong>Lower Tier:</strong> {selectedMember.LowerTier}</Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                </Grid>
              )}
            </Grid>

            {/* Action Buttons */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              marginTop: 3,
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              gap: { xs: 1, sm: 2 }
            }}>
              <Button
                variant="outlined"
                startIcon={<PhoneIcon />}
                href={`tel:${selectedMember.Mobile}`}
                sx={{ 
                  color: '#6d2057', 
                  borderColor: '#6d2057',
                  width: { xs: '100%', sm: 'auto' },
                  maxWidth: { xs: '200px', sm: 'none' }
                }}
              >
                Call Member
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {!selectedMember && (
        <Paper elevation={2} sx={{ padding: 4, textAlign: 'center', marginTop: 3 }}>
          <Typography variant="h6" color="textSecondary">
            Search and select a member to view their detailed profile
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ marginTop: 1 }}>
            You can search by name, mobile number, or member type (gym/pool)
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default MemberSearch;
